/** @file templateClipActions: the server store's "Import template clips" — import, refresh, toast. */
import type { EditorActions } from '../state/editorTypes';
import { asPose3DError } from './asPose3DError';
import { errorText } from './errorText';
import { importToast } from './importToast';
import { readHistory } from './readHistory';
import type { ServerContext } from './serverContext';

/** Puts the first imported clip on the playhead when none is (a new puppet starts with no clip). */
function showFirst(ctx: ServerContext, imported: readonly string[]): void {
  const first = imported[0];
  if (first !== undefined && ctx.get().playhead.clip === null) ctx.get().setPlayhead({ clip: first, frame: 0, playing: false });
}

/**
 * `importTemplateClips` runs after the batches already queued (`ctx.chain`): `replace: false`, then the document is
 * resynced (the debounced refresh: document, preview, lint) and the history read; a failure sets `lastError` and toasts.
 */
export function templateClipActions(ctx: ServerContext): Required<Pick<EditorActions, 'importTemplateClips'>> {
  const job = async (id: string, token: number): Promise<void> => {
    try {
      const call = ctx.api.puppets.templateClipsImport ?? (() => Promise.reject(new Error('template clips: not offered by this server (update it)')));
      const reply = await call(id, { replace: false });
      if (token !== ctx.token) return;
      ctx.dirty = true;
      await ctx.schedule.schedule();
      if (token !== ctx.token) return;
      showFirst(ctx, reply.imported);
      ctx.get().notify(reply.skipped.length > 0 ? 'warning' : 'info', importToast(reply));
      await readHistory(ctx, id, token);
    } catch (e) {
      if (token !== ctx.token) return;
      const text = errorText(asPose3DError(e));
      ctx.set((s) => ({ puppet: { ...s.puppet, lastError: text } }));
      ctx.get().notify('error', `Could not import the template clips: ${text}`);
    }
  };
  const importTemplateClips = (): Promise<void> => {
    const id = ctx.get().puppet.id;
    if (id === null) return Promise.resolve();
    const token = ctx.token;
    const run = ctx.chain.then(() => job(id, token));
    ctx.chain = run.catch(() => undefined);
    return run;
  };
  return { importTemplateClips };
}
